import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  LoadingController,
  ModalController,
} from "ionic-angular";
import moment from "moment";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";
import {
  ContractPeriod,
  DisciplineConfigMap,
  HospiceVisitPayerRates,
  MedicareDisciplineRow,
  PerBillableUnitRate,
  PerVisitRate,
} from "../models";

// CO-3336: wire values shared with payer-functions
// (lib/constants CHARGE_METHOD_*).
const CHARGE_METHOD_PER_VISIT = "PER_VISIT";
const CHARGE_METHOD_PER_BILLABLE_UNIT = "PER_BILLABLE_UNIT";

@Component({
  selector: "hospice-visits",
  templateUrl: "./hospice-visits.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesHospiceVisits),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesHospiceVisits),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesHospiceVisits implements OnInit, OnDestroy {
  contractPeriodsOptions: ContractPeriod[];
  _saveEnabled: boolean = false;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  currentContractPeriod: any = null;

  @Input() isActive: boolean = false;
  @Input() currentPayer: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onRatesSaved = new EventEmitter<any>();

  showHospicePerUnitTable: boolean = true;
  showHospicePerVisitTable: boolean = true;

  showPerUnitDetailsSection: boolean = false;
  showPerVisitDetailsSection: boolean = false;
  editMode: boolean = false;

  previousContractPeriod: any;

  hospicePerVisitGIPCareSetup: any = [
    {
      Rate: "",
      Description: "RN Visit",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "LVN/LPN Visit",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "Social Worker Visit",
      Disabled: "false",
    },
  ];

  hospicePerBillableUnitSetup: any = [
    {
      Rate: "",
      Description: "RN Visit",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "LVN/LPN Visit",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "Social Worker Visit",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "Social Worker Phone Call",
      Disabled: "false",
    },
    {
      Rate: "",
      Description: "Hospice Aide Visit",
      Disabled: "false",
    },
  ];

  // CO-3336 (epic CO-1390): Medicare renders per-discipline rows
  // (Billable toggle | Charge Method | Rate) instead of the two-table
  // layout. Reachable only when CO-3334's medicare_hospice_visit_billing
  // flag exposed the tab; Commercial/Medicaid keep the legacy layout.
  medicareDisciplineSetup: MedicareDisciplineRow[] =
    this.emptyMedicareDisciplineSetup();
  medicareValidationErrors: string[] = [];

  chargeMethodPerVisit = CHARGE_METHOD_PER_VISIT;
  chargeMethodPerBillableUnit = CHARGE_METHOD_PER_BILLABLE_UNIT;

  get isMedicare(): boolean {
    return (
      this.currentPayer &&
      this.currentPayer.payerType &&
      this.currentPayer.payerType.toString().toUpperCase() === "MEDICARE"
    );
  }

  // CO-3550: passive informational notice — the tab stays fully editable
  // when Requirements > Visits is Off (matching the CO-622 gate for
  // Commercial/Medicaid: configured rates simply don't reach claims), but
  // for Medicare we surface that fact inline. Reads the payer record's
  // saved complianceRules.includeVisits (the same field Requirements
  // reads/writes); only an explicit false shows the notice — an unsaved/
  // absent Requirements record does not.
  get showVisitsOffNotice(): boolean {
    return (
      this.isMedicare &&
      this.currentPayer.complianceRules &&
      this.currentPayer.complianceRules.includeVisits === false
    );
  }

  emptyMedicareDisciplineSetup(): MedicareDisciplineRow[] {
    // REQ-006: a never-configured discipline defaults to not billable.
    return [
      {
        Key: "RN",
        Label: "RN",
        Service: "RN",
        Description: "RN Visit",
        Billable: false,
        Method: null,
        Rate: "",
      },
      {
        Key: "LN",
        Label: "LVN/LPN",
        Service: "LN",
        Description: "LVN/LPN Visit",
        Billable: false,
        Method: null,
        Rate: "",
      },
      {
        Key: "SW",
        Label: "Social Worker",
        Service: "SW",
        Description: "Social Worker Visit",
        Billable: false,
        Method: null,
        Rate: "",
      },
      {
        Key: "SW_PHONECALL",
        Label: "Social Worker Phone Call",
        Service: "SW-PHONECALL",
        Description: "Social Worker Phone Call",
        Billable: false,
        Method: null,
        Rate: "",
      },
      {
        Key: "HA",
        Label: "Hospice Aide",
        Service: "HA",
        Description: "Hospice Aide Visit",
        Billable: false,
        Method: null,
        Rate: "",
      },
    ];
  }

  constructor(
    public modalCtrl: ModalController,
    private payersService: PayersService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController
  ) {
    if (this.currentContractPeriod == null) {
      this.currentContractPeriod = this.emptyContractPeriod();
    }
  }

  ngOnChanges(changes) {
    this.contractPeriodsOptions = [];
    if (this.isActive) {
      if (
        this.currentPayer &&
        this.currentPayer.payerId &&
        this.currentPayer.payerId.length > 0 &&
        this.currentPayer.id != 0
      ) {
        this.getPayerContractPeriods(true);
      } else {
        this.loadOptions();
      }
    }
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {}

  onPeriodChanged() {
    this.getPayerContractPeriods(false);
    this.currentContractPeriod = null;
    this.showPerVisitDetailsSection = false;
    this.showPerUnitDetailsSection = false;
  }

  emptyContractPeriod() {
    return null;
  }

  loadOptions(): void {
    let addNewContractPeriodOption: ContractPeriod = {
      id: 0,
      name: "Add New",
      value: "Add New",
      effectiveDate: "",
      expiredDate: "",
    };
    this.contractPeriodsOptions = [
      addNewContractPeriodOption,
      ...this.contractPeriodsOptions,
    ];
  }

  showSavePayerModal() {
    // CO-3336: reject an invalid Medicare configuration before the
    // confirmation modal — nothing to confirm if the save can't happen.
    if (this.isMedicare && !this.validateMedicareRows()) {
      return;
    }
    if (this.editMode) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveVisitRates();
        }
      });
      modalPage.present();
    } else {
      this.saveVisitRates();
    }
  }

  saveVisitRates() {
    if (this.isMedicare) {
      this.saveMedicareVisitRates();
      return;
    }
    this.hospicePerBillableUnitSetup = this.setEmptyRatesToDefaultValue(
      this.hospicePerBillableUnitSetup
    );
    this.hospicePerVisitGIPCareSetup = this.setEmptyRatesToDefaultValue(
      this.hospicePerVisitGIPCareSetup
    );
    let payerRates = this.mapNewHospiceVisitRate(
      null,
      this.currentContractPeriod.effectiveDate,
      this.currentContractPeriod.expiredDate
    );
    payerRates = this.mapRatesValuesToSave(payerRates);
    this.saveNewPayerContractPeriod(
      payerRates,
      "Rates saved successfully.",
      true
    );
  }

  // CO-3336: validate the Medicare rows per REQ-008 — a billable
  // discipline needs a method and a rate greater than $0; "no charge"
  // means turning Billable off, never entering $0. No upper bound.
  // Returns true when the configuration is saveable.
  validateMedicareRows(): boolean {
    this.medicareValidationErrors = [];
    this.medicareDisciplineSetup.forEach((row) => {
      row.Error = null;
      if (!row.Billable) {
        return;
      }
      if (!row.Method) {
        row.Error = "Select a charge method";
        this.medicareValidationErrors.push(
          row.Label + " — select a charge method"
        );
        return;
      }
      const rate = Number(row.Rate);
      if (!(rate > 0)) {
        row.Error = "Must be greater than $0";
        this.medicareValidationErrors.push(
          row.Label + " — rate must be greater than $0"
        );
      }
    });
    return this.medicareValidationErrors.length === 0;
  }

  saveMedicareVisitRates() {
    if (!this.validateMedicareRows()) {
      return;
    }

    const perVisit: PerVisitRate = {};
    const perBillableUnit: PerBillableUnitRate = {} as PerBillableUnitRate;
    const disciplineConfig: DisciplineConfigMap = {};

    this.medicareDisciplineSetup.forEach((row) => {
      disciplineConfig[row.Key] = {
        billable: row.Billable,
        chargeMethod: row.Method || undefined,
      };
      if (!row.Billable) {
        return;
      }
      const detail = {
        Rate: Number(row.Rate),
        Description: row.Description,
        Service: row.Service,
      };
      if (row.Method === CHARGE_METHOD_PER_VISIT) {
        perVisit[row.Key] = detail;
      } else {
        perBillableUnit[row.Key] = detail;
      }
    });

    const payload: HospiceVisitPayerRates = {
      payerId: this.currentPayer ? this.currentPayer.payerId : null,
      benefitSubtype: "VISITS",
      benefitType: "HOSPICE",
      payerType: this.currentPayer
        ? this.currentPayer.payerType.toUpperCase()
        : null,
      rates: [{ PerVisit: perVisit, PerBillableUnit: perBillableUnit }],
      start: this.currentContractPeriod.effectiveDate,
      end: this.currentContractPeriod.expiredDate,
      disciplineConfig: disciplineConfig,
      periodName: this.currentContractPeriod.periodName || undefined,
    };

    this.saveNewPayerContractPeriod(payload, "Rates saved successfully.", true);
  }

  setMedicareMethod(row: MedicareDisciplineRow, method: string) {
    row.Method = method;
  }

  // CO-3337 (REQ-026): per-period rate history. Loaded lazily on expand —
  // one getPayerRates call per known contract period.
  showMedicareRateHistory = false;
  medicareRateHistory: {
    label: string;
    effectiveDate: string;
    expiredDate: string;
    entries: { label: string; summary: string }[];
  }[] = [];

  toggleMedicareRateHistory() {
    this.showMedicareRateHistory = !this.showMedicareRateHistory;
    if (this.showMedicareRateHistory) {
      this.loadMedicareRateHistory();
    }
  }

  loadMedicareRateHistory() {
    this.medicareRateHistory = [];
    const periods = this.contractPeriodsOptions.filter(
      (option) => option.value != "Add New"
    );
    periods.forEach((period) => {
      this.payersService
        .getPayerRates(
          this.currentPayer.payerId,
          "HOSPICE",
          period.effectiveDate,
          "VISITS"
        )
        .pipe()
        .take(1)
        .subscribe(
          (record) => {
            this.medicareRateHistory.push({
              label: period.name,
              effectiveDate: period.effectiveDate,
              expiredDate: period.expiredDate,
              entries: this.summarizeMedicareRecord(record),
            });
            this.medicareRateHistory.sort((a, b) =>
              b.effectiveDate.localeCompare(a.effectiveDate)
            );
          },
          () => {
            // A period with no readable record still shows in history as
            // fully unconfigured rather than silently vanishing.
            this.medicareRateHistory.push({
              label: period.name,
              effectiveDate: period.effectiveDate,
              expiredDate: period.expiredDate,
              entries: this.summarizeMedicareRecord(null),
            });
          }
        );
    });
  }

  summarizeMedicareRecord(
    record
  ): { label: string; summary: string }[] {
    const config: DisciplineConfigMap =
      (record && record.disciplineConfig) || {};
    const groups =
      record && record.rates && record.rates.length > 0
        ? record.rates[0]
        : { PerVisit: {}, PerBillableUnit: {} };

    return this.emptyMedicareDisciplineSetup().map((row) => {
      const cfg = config[row.Key];
      if (!cfg || !cfg.billable || !cfg.chargeMethod) {
        return { label: row.Label, summary: "Not billable" };
      }
      const isPerVisit = cfg.chargeMethod === CHARGE_METHOD_PER_VISIT;
      const group = isPerVisit ? groups.PerVisit : groups.PerBillableUnit;
      const detail = group && group[row.Key];
      const rate =
        detail && detail.Rate != null
          ? "$" + this.formatToTwoDecimals(Number(detail.Rate))
          : "—";
      return {
        label: row.Label,
        summary: isPerVisit
          ? "Per Flat Rate — " + rate + " / visit"
          : "Per Billable Unit — " + rate + " / 15-min unit",
      };
    });
  }

  onMedicareBillableChanged(row: MedicareDisciplineRow) {
    if (!row.Billable) {
      row.Error = null;
    }
  }

  mapRatesValuesToSave(hospiceVisitPayerRates: HospiceVisitPayerRates) {
    let perVisitRateValues: PerVisitRate = {
      RN: {
        Rate: Number(this.hospicePerVisitGIPCareSetup[0].Rate),
        Description: this.hospicePerBillableUnitSetup[0].Description,
        Service: "RN",
      },
      LN: {
        Rate: Number(this.hospicePerVisitGIPCareSetup[1].Rate),
        Description: this.hospicePerBillableUnitSetup[1].Description,
        Service: "LN",
      },
      SW: {
        Rate: Number(this.hospicePerVisitGIPCareSetup[2].Rate),
        Description: this.hospicePerBillableUnitSetup[2].Description,
        Service: "SW",
      },
    };

    let perBillableUnitValues: PerBillableUnitRate = {
      RN: {
        Rate: Number(this.hospicePerBillableUnitSetup[0].Rate),
        Description: this.hospicePerBillableUnitSetup[0].Description,
        Service: "RN",
      },
      LN: {
        Rate: Number(this.hospicePerBillableUnitSetup[1].Rate),
        Description: this.hospicePerBillableUnitSetup[1].Description,
        Service: "LN",
      },
      SW: {
        Rate: Number(this.hospicePerBillableUnitSetup[2].Rate),
        Description: this.hospicePerBillableUnitSetup[2].Description,
        Service: "SW",
      },
      SW_PHONECALL: {
        Rate: Number(this.hospicePerBillableUnitSetup[3].Rate),
        Description: this.hospicePerBillableUnitSetup[3].Description,
        Service: "SW-PHONECALL",
      },
      HA: {
        Rate: Number(this.hospicePerBillableUnitSetup[4].Rate),
        Description: this.hospicePerBillableUnitSetup[4].Description,
        Service: "HA",
      },
    };

    hospiceVisitPayerRates.rates = [
      {
        PerVisit: perVisitRateValues,
        PerBillableUnit: perBillableUnitValues,
      },
    ];

    return hospiceVisitPayerRates;
  }

  setEmptyRatesToDefaultValue(ratesArray) {
    ratesArray.map((rate) => {
      rate.Rate = rate.Rate == "" ? "0.01" : rate.Rate;
      return rate;
    });
    return ratesArray;
  }

  cancel() {
    this.onCanceled.emit(true);
  }

  setHospicePerVisitRates(value) {
    this.hospicePerVisitGIPCareSetup = value;
  }

  setHospicePerUnitRates(value) {
    this.hospicePerBillableUnitSetup = value;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  onCollapseClicked(event, tableName) {
    switch (tableName) {
      case "PerVisitDetails":
        this.showHospicePerVisitTable = event.isOpen;
        break;
      case "PerUnitDetails":
        this.showHospicePerUnitTable = event.isOpen;
        break;
    }
  }

  async onContractPeriodChange(event: any) {
    if (!event) {
      this.currentContractPeriod = this.emptyContractPeriod();
      this._saveEnabled = false;
      this.previousContractPeriod = this.currentContractPeriod;
      this.clearContractPeriodRates();
      this.setRatesDisabledToEdit();
      this.showPerUnitDetailsSection = false;
      return;
    }
    if (event.value == "Add New") {
      this.addNewContractPeriod();
    } else {
      this.currentContractPeriod = {
        id: event.id,
        name: event.name,
        value: event.value,
        expiredDate: event.expiredDate,
        effectiveDate: event.effectiveDate,
      };
      this.getPayerRatesByPeriod(this.currentContractPeriod.effectiveDate);
      this.previousContractPeriod = this.currentContractPeriod;
      this._saveEnabled = true;
    }
  }

  onContractPeriodClear(event: any) {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.id != 0
    );
    this.loadOptions();
  }

  addNewContractPeriod() {
    if (this.isMedicare) {
      this.addNewMedicareContractPeriod();
      return;
    }
    let minDate: moment.Moment = moment("2023-09-30", this.dateFormat);
    this.contractPeriodsOptions.forEach((contractPeriod) => {
      let periodStart = moment(contractPeriod.effectiveDate);
      if (periodStart.isAfter(minDate)) {
        minDate = periodStart;
      }
    });

    let contractPeriodModal = this.modalCtrl.create(
      "ContractPeriodModalComponent",
      { minDateFormat: minDate },
      { cssClass: "contract-period-modal" }
    );

    contractPeriodModal.onDidDismiss((data) => {
      if (data) {
        let newOption = this.formatContractPeriodModel(data);
        let contractPeriods = this.contractPeriodsOptions.filter(
          (contract) => contract.value != "Add New"
        );
        if (
          contractPeriods.length === 0 ||
          newOption.effectiveDate >
            this.getMostRecentContractPeriod(contractPeriods).effectiveDate
        ) {
          this.saveNewContractPeriodOption(newOption);
        } else {
          this.showAlert({
            title: "Error",
            message:
              "Contract period’s effective date can’t be before or equal to the active period’s date.",
          });
          this.onNewPeriodCanceled();
        }
      } else {
        this.onNewPeriodCanceled();
      }
    });
    contractPeriodModal.present();
  }

  // CO-3337: the Medicare Add Contract Period modal collects a name and
  // both dates and enforces no-overlap (REQ-011) — which also allows
  // back-filling an earlier fiscal year, so the legacy "must be newer
  // than the latest period" rule deliberately does not apply here.
  addNewMedicareContractPeriod() {
    let contractPeriodModal = this.modalCtrl.create(
      "ContractPeriodModalComponent",
      {
        title: "Add Contract Period",
        medicarePeriodMode: true,
        existingPeriods: this.contractPeriodsOptions
          .filter((option) => option.value != "Add New")
          .map((option) => ({
            name: option.periodName || option.name,
            effectiveDate: option.effectiveDate,
            expiredDate: option.expiredDate,
          })),
      },
      { cssClass: "contract-period-modal" }
    );

    contractPeriodModal.onDidDismiss((data) => {
      if (data && data.effectiveDate) {
        this.saveNewContractPeriodOption(
          this.formatMedicareContractPeriodModel(data)
        );
      } else {
        this.onNewPeriodCanceled();
      }
    });
    contractPeriodModal.present();
  }

  formatMedicareContractPeriodModel(data): ContractPeriod {
    return {
      id: 0,
      name: this.medicarePeriodDisplayName(
        data.name,
        data.effectiveDate,
        data.expiredDate
      ),
      value: moment(data.effectiveDate, this.dateFormat).format(
        this.dateUIFormat
      ),
      effectiveDate: data.effectiveDate,
      expiredDate: data.expiredDate,
      periodName: data.name,
    };
  }

  medicarePeriodDisplayName(
    periodName: string,
    effectiveDate: string,
    expiredDate: string
  ): string {
    let dates = moment(effectiveDate, this.dateFormat).format(
      this.dateUIFormat
    );
    if (expiredDate && expiredDate != "") {
      dates =
        dates +
        " - " +
        moment(expiredDate, this.dateFormat).format(this.dateUIFormat);
    }
    return periodName ? periodName + " (" + dates + ")" : dates;
  }

  onNewPeriodCanceled() {
    this.currentContractPeriod =
      this.previousContractPeriod &&
      this.previousContractPeriod.value != "Add New"
        ? this.previousContractPeriod
        : this.emptyContractPeriod();
  }

  saveNewContractPeriodOption(newOption) {
    this.contractPeriodsOptions = [newOption, ...this.contractPeriodsOptions];
    this.currentContractPeriod = newOption;
    this.clearContractPeriodRates();
    this.removeNewContractPeriodOption();
    this._saveEnabled = true;
    this.showPerUnitDetailsSection = true;
  }

  removeNewContractPeriodOption() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.value != "Add New"
    );
  }

  formatContractPeriodModel(period: any): ContractPeriod {
    let newContractPeriod = moment(new Date(period)).format(this.dateUIFormat);
    let newEffectiveDate = moment(new Date(period)).format(this.dateFormat);
    return {
      id: 0,
      name: newContractPeriod,
      value: newContractPeriod,
      effectiveDate: newEffectiveDate,
      expiredDate: null,
    };
  }

  getPayerContractPeriods(firstLoad) {
    let spinnerText = "Loading Contract Periods";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.payersService
      .getPayerRatesPeriods(this.currentPayer.payerId, "HOSPICE", "VISITS")
      .pipe()
      .take(1)
      .subscribe((response) => {
        spinner.dismiss();
        let previousContractPeriods: ContractPeriod[] = [];
        if (response !== null) {
          let contractPeriod = Object.values(response);
          let count = 1;
          contractPeriod.forEach((period) => {
            let periodStart = moment(period.start);
            let name = periodStart.format(this.dateUIFormat);
            if (period.end && period.end != "") {
              name =
                name + " - " + moment(period.end).format(this.dateUIFormat);
            }
            // CO-3337: a named Medicare period displays as
            // "FY2027 (10/01/2026 - 09/30/2027)".
            if (period.periodName) {
              name = period.periodName + " (" + name + ")";
            }
            let newPeriod = {
              id: count,
              name: name,
              value: periodStart.format(this.dateUIFormat),
              effectiveDate: periodStart.format(this.dateFormat),
              expiredDate: period.end
                ? moment(period.end).format(this.dateFormat)
                : "",
              periodName: period.periodName,
            };

            previousContractPeriods.push(newPeriod);
            count++;
          });
        }
        this.contractPeriodsOptions = previousContractPeriods.sort(
          (a, b) =>
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
        );
        this.loadOptions();
      });
  }

  getMostRecentContractPeriod(contractPeriods) {
    return contractPeriods.sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime()
    )[0];
  }

  getPayerRatesByPeriod(period) {
    if (this.currentPayer) {
      let spinnerText = "Loading Contract Period Rates";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();
      this.payersService
        .getPayerRates(this.currentPayer.payerId, "HOSPICE", period, "VISITS")
        .pipe()
        .take(1)
        .subscribe(
          (response) => {
            spinner.dismiss();
            this.editMode = true;
            this.clearContractPeriodRates();
            this.mapContractPeriodRates(response);
            this.showPerUnitDetailsSection = true;
          },
          (error) => {
            spinner.dismiss();
            this.clearContractPeriodRates();
            this.showPerUnitDetailsSection = false;
            this.showError(error);
          }
        );
    }
  }

  clearContractPeriodRates() {
    this.medicareDisciplineSetup = this.emptyMedicareDisciplineSetup();
    this.medicareValidationErrors = [];
    this.hospicePerBillableUnitSetup = [
      {
        Rate: "",
        Description: "RN Visit",
      },
      {
        Rate: "",
        Description: "LVN/LPN Visit",
      },
      {
        Rate: "",
        Description: "Social Worker Visit",
      },
      {
        Rate: "",
        Description: "Social Worker Phone Call",
      },
      {
        Rate: "",
        Description: "Hospice Aide Visit",
      },
    ];

    this.hospicePerVisitGIPCareSetup = [
      {
        Rate: "",
        Description: "RN Visit",
      },
      {
        Rate: "",
        Description: "LVN/LPN Visit",
      },
      {
        Rate: "",
        Description: "Social Worker Visit",
      },
    ];
  }

  mapContractPeriodRates(ratesData) {
    if (this.isMedicare) {
      this.mapMedicareDisciplineRows(ratesData);
      return;
    }
    if (ratesData && ratesData.rates && ratesData.rates.length > 0) {
      this.hospicePerBillableUnitSetup.forEach((item) => {
        const match = Object.values(ratesData.rates[0].PerBillableUnit).find(
          (rateObj) => rateObj["Description"] === item.Description
        );
        if (match) {
          item.Rate = this.formatToTwoDecimals(Number(match["Rate"]));
          item.Disabled = false;
        }
      });

      this.hospicePerVisitGIPCareSetup.forEach((item) => {
        const match = Object.values(ratesData.rates[0].PerVisit).find(
          (rateObj) => rateObj["Description"] === item.Description
        );
        if (match) {
          item.Rate = this.formatToTwoDecimals(Number(match["Rate"]));
          item.Disabled = false;
        }
      });
    }
  }

  // CO-3336: populate the Medicare rows from a stored record. The
  // disciplineConfig group is authoritative for billable/method; the
  // dollar value comes from the selected method's rate group. A
  // discipline absent from disciplineConfig stays not billable.
  mapMedicareDisciplineRows(ratesData) {
    const config: DisciplineConfigMap =
      (ratesData && ratesData.disciplineConfig) || {};
    const groups =
      ratesData && ratesData.rates && ratesData.rates.length > 0
        ? ratesData.rates[0]
        : { PerVisit: {}, PerBillableUnit: {} };

    this.medicareDisciplineSetup.forEach((row) => {
      const cfg = config[row.Key];
      row.Error = null;
      row.Billable = !!(cfg && cfg.billable);
      row.Method = cfg && cfg.chargeMethod ? cfg.chargeMethod : null;
      row.Rate = "";
      if (row.Billable && row.Method) {
        const group =
          row.Method === CHARGE_METHOD_PER_VISIT
            ? groups.PerVisit
            : groups.PerBillableUnit;
        const detail = group && group[row.Key];
        if (detail && detail.Rate != null) {
          row.Rate = this.formatToTwoDecimals(Number(detail.Rate));
        }
      }
    });
  }

  setRatesDisabledToEdit() {
    this.hospicePerBillableUnitSetup = this.hospicePerBillableUnitSetup.map(
      (option) => {
        option.Disabled = true;
        return option;
      }
    );

    this.hospicePerVisitGIPCareSetup = this.hospicePerVisitGIPCareSetup.map(
      (option) => {
        option.Disabled = true;
        return option;
      }
    );
  }

  formatToTwoDecimals(num: number): string {
    return num.toFixed(2);
  }

  saveNewPayerContractPeriod(
    hospiceVisitRatesData: HospiceVisitPayerRates,
    saveMessage: string,
    saveRate: boolean
  ) {
    if (this.currentPayer) {
      let spinnerText = "Saving Hospice Visit Rates";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();
      this.payersService
        .postSavePayerRates(hospiceVisitRatesData)
        .pipe()
        .take(1)
        .subscribe(
          () => {
            spinner.dismiss();
            this.contractPeriodsOptions = [];
            this.getPayerContractPeriods(false);
            this.onSaveSuccessAlert(saveMessage);
            this.loadOptions();
            this.previousContractPeriod = this.currentContractPeriod;
            if (saveRate) {
              this.onRatesSaved.emit();
            }
          },
          (error) => {
            spinner.dismiss();
            this.showError(error);
          }
        );
    } else {
      this.currentContractPeriod = this.emptyContractPeriod();
      this.contractPeriodsOptions = [];
      this.loadOptions();
      this.showAlert({
        title: "Error",
        message:
          "Payer general information needs to be saved before adding a new contract period.",
      });
    }
  }

  showError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  showAlert = ({ title, message }) => {
    let alert = this.alertCtrl.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  mapNewHospiceVisitRate(rates, start, end) {
    let newVisitRates: HospiceVisitPayerRates = {
      payerId: this.currentPayer ? this.currentPayer.payerId : null,
      benefitSubtype: "VISITS",
      benefitType: "HOSPICE",
      payerType: this.currentPayer
        ? this.currentPayer.payerType.toUpperCase()
        : null,
      rates: rates,
      start: start,
      end: end,
    };
    return newVisitRates;
  }

  private onSaveSuccessAlert = (message: string) => {
    this.showAlert({
      title: "Success",
      message: message,
    });
  };

  getEffectiveDate() {
    return this.currentContractPeriod &&
      this.currentContractPeriod.effectiveDate
      ? this.formatDate(this.currentContractPeriod.effectiveDate)
      : "";
  }

  getExpiredDate() {
    return this.currentContractPeriod && this.currentContractPeriod.expiredDate
      ? this.formatDate(this.currentContractPeriod.expiredDate)
      : "";
  }

  formatDate(dateString: string): string {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    const formattedMonth = (date.getMonth() + 1).toString().padStart(2, "0");
    const formattedDay = date.getDate().toString().padStart(2, "0");
    const formattedYear = date.getFullYear();

    return `${formattedMonth}/${formattedDay}/${formattedYear}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/rates-payer/hospice-visits/hospice-visits.ts